import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2, Clock, CheckCircle, UserCheck, MapPin, CornerDownRight,
  User, Euro, RefreshCw, ChevronRight
} from 'lucide-react';

const API_URL = config.API_URL;
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

const FLEET_TABS = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending_proposal', label: 'En attente' },
  { key: 'driver_assigned', label: 'Attribuées' },
  { key: 'driver_accepted', label: 'Acceptées' },
  { key: 'completed', label: 'Terminées' },
];

const STATUS_CONFIG = {
  pending_proposal: { label: 'En attente d\'attribution', cls: 'bg-amber-500/20 text-amber-400', icon: Clock },
  driver_assigned: { label: 'Chauffeur attribué', cls: 'bg-purple-500/20 text-purple-400', icon: UserCheck },
  driver_accepted: { label: 'Chauffeur accepté', cls: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  completed: { label: 'Terminée', cls: 'bg-gray-500/20 text-gray-400', icon: CheckCircle },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const fmtCurrency = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

export function PartnerMissionsTab() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/partner/missions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMissions(data.missions || []);
    } catch { setMissions([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMissions(); }, [fetchMissions]);

  const filtered = filter === 'all' ? missions : missions.filter(m => m.fleet_status === filter);

  const counts = {};
  for (const m of missions) { counts[m.fleet_status] = (counts[m.fleet_status] || 0) + 1; }

  return (
    <div className="space-y-4" data-testid="partner-missions-tab">
      {/* Filter tabs */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
        {FLEET_TABS.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              filter === t.key
                ? 'bg-[#C9A24D] text-[#0B0B0B]'
                : 'bg-[#1A1A1A] text-[#E5E5E5]/50 hover:text-white'
            }`}
            data-testid={`mission-filter-${t.key}`}>
            {t.label} {counts[t.key] ? <span className="ml-1 text-[10px]">({counts[t.key]})</span> : t.key === 'all' ? <span className="ml-1 text-[10px]">({missions.length})</span> : null}
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={fetchMissions} className="text-[#E5E5E5]/40 hover:text-[#C9A24D] h-7 ml-auto shrink-0" data-testid="refresh-missions">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Mission list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#1F1F1F] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <p className="text-[#666] text-sm">Aucune mission {filter !== 'all' ? `avec le statut "${FLEET_TABS.find(t => t.key === filter)?.label}"` : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => {
            const st = STATUS_CONFIG[m.fleet_status] || { label: m.fleet_status || '—', cls: 'bg-gray-500/20 text-gray-400', icon: Clock };
            const StIcon = st.icon;
            return (
              <Card key={m.id} className="bg-[#1F1F1F] border-[#2A2A2A] hover:border-[#C9A24D]/20 transition-colors" data-testid={`mission-card-${m.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                        <StIcon className="w-3 h-3" />{st.label}
                      </span>
                      <span className="text-[10px] text-white/20 uppercase">{m.type}</span>
                    </div>
                    <span className="text-[10px] sm:text-xs text-white/30 shrink-0">{formatDate(m.start_time)}</span>
                  </div>

                  {/* Addresses */}
                  <div className="space-y-1 mb-2">
                    <div className="flex items-start gap-1.5">
                      <MapPin className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-xs sm:text-sm text-white/70 truncate">{m.pickup_address || '—'}</p>
                    </div>
                    {m.dropoff_address && (
                      <div className="flex items-start gap-1.5">
                        <CornerDownRight className="w-3 h-3 text-white/20 mt-0.5 shrink-0" />
                        <p className="text-[10px] sm:text-xs text-white/40 truncate">{m.dropoff_address}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer info */}
                  <div className="flex items-center justify-between text-[10px] sm:text-xs">
                    <div className="flex items-center gap-3 text-white/30">
                      {m.client_name && <span>{m.client_name}{m.passenger_count > 0 ? ` · ${m.passenger_count} pax` : ''}</span>}
                      {m.driver_name && (
                        <span className="flex items-center gap-1 text-[#C9A24D]/70">
                          <User className="w-3 h-3" />{m.driver_name}
                        </span>
                      )}
                    </div>
                    {m.driver_fee && (
                      <span className="text-[#C9A24D] font-semibold flex items-center gap-0.5">
                        <Euro className="w-3 h-3" />{fmtCurrency(m.driver_fee)}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
